import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { ViewContext } from '@localagent/sdk';
import { configureAtomDevtools } from '../utils/tracking';

/**
 * What a view publishes about itself so the docked agent can be told what is on
 * screen (app-shell ADR-014).
 *
 * A snapshot must be published by the LEAF that renders the records — the lane
 * groupings and the org chart's expanded set live in component-local state that
 * nothing above them can read — so this atom is the channel between that leaf
 * and the panel, which sits in a disjoint position in the tree.
 */
export interface ShogunViewSnapshot {
  /** The pathname this snapshot describes; a snapshot for another route is ignored. */
  route: string;
  /** Everything except `route` and `captured_at`, which the sender owns. */
  context: Omit<ViewContext, 'route' | 'captured_at'>;
  /** Offered by an empty view; filled into the composer, never auto-sent. Client-only. */
  starterPrompts?: string[];
}

export const shogunViewSnapshotAtom = configureAtomDevtools(
  atom<ShogunViewSnapshot | null>(null),
  'shogun/viewSnapshot'
);

/**
 * The panel's remembered conversations, keyed by the route-supplied default
 * agent (`default` where a route names none). The panel unmounts on the chat
 * route (app-shell ADR-020), so without this the assistant would mint a new
 * thread on every visit; with it, the one long-lived assistant thread
 * survives. It is keyed rather than single because a view naming its own agent
 * is having a different conversation: carrying the assistant thread into search
 * would hand it a history about org charts, and carrying search back out would
 * do the reverse.
 */
export const panelChatSessionIdsAtom = configureAtomDevtools(
  atomWithStorage<Record<string, string>>('localagent-panel-chat-sessions', {}),
  'shogun/panelChatSessionIds'
);

/** A one-shot hand-off of composer text from a view's starter prompt to the panel. */
export const shogunComposerDraftAtom = configureAtomDevtools(
  atom<string | null>(null),
  'shogun/composerDraft'
);
